import PropTypes from "prop-types";
import { useState } from "react";
import { useIntl } from "react-intl";
import ChatIcon from "@mui/icons-material/Chat";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PublicIcon from "@mui/icons-material/Public";
import { makeStyles } from "@mui/styles";
import { MailToLink, TelLink } from "../../../common/Links";
import DirectMessageRowItem from "./DirectMessageRowItem";
import FaIcon from "./FaIcon";
import GroupRowItem from "./GroupRowItem";
import RowItem from "./RowItem";

const useStyles = makeStyles(() => ({
  icon: {
    fontSize: 18,
    color: "rgba(0,0,0,0.42)",
  },
  actionIcon: {
    fontSize: 18,
    color: "rgba(0,0,0,1)",
  },
}));

const plusIcon = "far fa-plus-square";

const UserInformationColumn = ({
  accountSettings,
  handleOpenPrivacySetting,
  setAccountSettings,
  visibility,
}) => {
  const classes = useStyles();
  const intl = useIntl();

  const [showInputField, setShowInputField] = useState(null);
  const [value, setValue] = useState(null);

  const {
    companyUserGroup,
    company,
    directMessage,
    email,
    mainEducation,
    mobile,
    phone,
    visibilitySettings = {},
  } = accountSettings || {};

  const handleAdd = (name) => {
    setAccountSettings((prev) => {
      let temp = { ...prev.visibilitySettings };
      temp[name] = "PUBLIC";
      return {
        ...prev,
        visibilitySettings: { ...temp }
      };
    });
    setShowInputField(name);
  };

  const handleCancel = () => {
    setShowInputField(null);
  };

  const handleChange = e => {
    setValue(e.target.value);
  };

  const handleDelete = (settingKey) => {
    let temp = { ...visibilitySettings };
    temp[settingKey] = "PUBLIC";
    setAccountSettings({
      ...accountSettings,
      [settingKey]: "",
      visibilitySettings: { ...temp }
    });
  };

  const handleSave = (actionType, editValue, index = null) => {
    setAccountSettings({
      ...accountSettings,
      [showInputField]: value || editValue,
    });

    setShowInputField(null);
    setValue(null);
  };

  return (
    <>
      <RowItem hideActionButtons
        title={company}
        subTitle={intl.formatMessage({ id: "settings.theUsersCompany" })}
        leftIcon={<FaIcon inputName="company" icon="fas fa-briefcase" />}
        privacyIcon={<PublicIcon />}
        actionIcon={<MoreHorizIcon className={classes.actionIcon} />}
        onClickActionIcon="company"
        onDelete={handleDelete}
        handleOpenPrivacySetting={handleOpenPrivacySetting}
        name="company"
        settingKey="company"
        iconLabel={intl.formatMessage({ id: "settings.theUsersCompany" })}
        showInputField={showInputField}
        handleCancel={handleCancel}
        handleSave={handleSave}
        handleChange={handleChange}
        privacyIconDisable={false}
        actionIconDisable={false}
        visibility={visibility}
        setShowInputField={setShowInputField}
      />
      <GroupRowItem
        leftIcon={<FaIcon inputName="company" icon="fas fa-sitemap" />}
        userGroupBreadCrumb={accountSettings?.userGroupBreadCrumb}
      />
      <RowItem hideActionButtons
        title={<MailToLink email={email} />}
        subTitle={intl.formatMessage({ id: "settings.email" })}
        leftIcon={<FaIcon inputName="email" icon="far fa-envelope" />}
        privacyIcon={<PublicIcon />}
        actionIcon={<MoreHorizIcon className={classes.actionIcon} />}
        onClickActionIcon="email"
        onDelete={handleDelete}
        handleOpenPrivacySetting={handleOpenPrivacySetting}
        name="email"
        settingKey="email"
        iconLabel={intl.formatMessage({ id: "settings.email" })}
        showInputField={showInputField}
        handleCancel={handleCancel}
        handleSave={handleSave}
        handleChange={handleChange}
        visibility={visibility}
        setShowInputField={setShowInputField}
      />
      <RowItem
        title={mainEducation}
        subTitle={intl.formatMessage({ id: "settings.professionalDegrees" })}
        leftIcon={
          <FaIcon
            handleAdd={handleAdd}
            inputName="mainEducation"
            icon={mainEducation ? "fas fa-graduation-cap" : plusIcon}
          />
        }
        privacyIcon={
          visibilitySettings.mainEducation === "PUBLIC" ? (
            <PublicIcon />
          ) : (
            <FaIcon icon="fas fa-lock" />
          )
        }
        actionIcon={<MoreHorizIcon className={classes.actionIcon} />}
        onClickActionIcon="mainEducation"
        onDelete={handleDelete}
        handleOpenPrivacySetting={handleOpenPrivacySetting}
        name="mainEducation"
        settingKey="mainEducation"
        iconLabel={intl.formatMessage({ id: "settings.professionalDegrees" })}
        showInputField={showInputField}
        handleCancel={handleCancel}
        handleSave={handleSave}
        handleChange={handleChange}
        visibility={visibility}
        setShowInputField={setShowInputField}
      />
      <RowItem
        title={<TelLink tel={phone} />}
        subTitle={intl.formatMessage({ id: "settings.phone" })}
        leftIcon={
          <FaIcon
            handleAdd={handleAdd}
            inputName="phone"
            icon={phone ? "fas fa-phone" : plusIcon}
          />
        }
        privacyIcon={
          visibilitySettings.phone === "PUBLIC" ? (
            <PublicIcon />
          ) : (
            <FaIcon icon="fas fa-lock" />
          )
        }
        actionIcon={<MoreHorizIcon className={classes.actionIcon} />}
        onClickActionIcon="phone"
        onDelete={handleDelete}
        handleOpenPrivacySetting={handleOpenPrivacySetting}
        name="phone"
        settingKey="phone"
        iconLabel={intl.formatMessage({ id: "settings.phone" })}
        showInputField={showInputField}
        handleCancel={handleCancel}
        handleSave={handleSave}
        handleChange={handleChange}
        visibility={visibility}
        setShowInputField={setShowInputField}
      />
      <RowItem
        title={<TelLink tel={mobile} />}
        subTitle={intl.formatMessage({ id: "settings.mobile" })}
        leftIcon={
          <FaIcon
            handleAdd={handleAdd}
            inputName="mobile"
            icon={mobile ? "fas fa-phone" : plusIcon}
          />
        }
        privacyIcon={
          visibilitySettings.mobile === "PUBLIC" ? (
            <PublicIcon />
          ) : (
            <FaIcon icon="fas fa-lock" />
          )
        }
        actionIcon={<MoreHorizIcon className={classes.actionIcon} />}
        onClickActionIcon="mobile"
        onDelete={handleDelete}
        handleOpenPrivacySetting={handleOpenPrivacySetting}
        name="mobile"
        settingKey="mobile"
        iconLabel={intl.formatMessage({ id: "settings.mobile" })}
        showInputField={showInputField}
        handleCancel={handleCancel}
        handleSave={handleSave}
        handleChange={handleChange}
        visibility={visibility}
        setShowInputField={setShowInputField}
      />
      <DirectMessageRowItem
        title={directMessage || email}
        subTitle={intl.formatMessage({ id: "settings.instantMessage" })}
        leftIcon={<ChatIcon className={classes.icon} sx={{ marginTop: "3px" }} />}
        privacyIcon={
          visibilitySettings.directMessage === "PUBLIC" ? (
            <PublicIcon />
          ) : (
            <FaIcon icon="fas fa-lock" />
          )
        }
        handleOpenPrivacySetting={handleOpenPrivacySetting}
        name="directMessage"
        visibility={visibility}
      />
    </>
  );
};

UserInformationColumn.defaultProps = {
  accountSettings: {},
  visibilitySettings: {},
};

UserInformationColumn.propTypes = {
  handleOpenPrivacySetting: PropTypes.func.isRequired,
  accountSettings: PropTypes.shape({
    companyUserGroup: PropTypes.string,
    company: PropTypes.string,
    mainEducation: PropTypes.string,
    phone: PropTypes.string,
    mobile: PropTypes.string,
    email: PropTypes.string,
    directMessage: PropTypes.string,
  }),
  visibilitySettings: PropTypes.shape({
    company: PropTypes.string,
    mainEducation: PropTypes.string,
    phone: PropTypes.string,
    mobile: PropTypes.string,
    email: PropTypes.string,
    directMessage: PropTypes.string,
  }),
  visibility: PropTypes.string.isRequired,
};

export default UserInformationColumn;
